import * as THREE from 'three';
import { PLAYER } from '../constants';
import { InputManager } from '../input/InputManager';

export class PlayerController {
  private camera: THREE.PerspectiveCamera;
  private input: InputManager;
  private velocity = new THREE.Vector3();
  private euler = new THREE.Euler(0, 0, 0, 'YXZ');

  // State
  isSprinting = false;
  isCrouching = false;
  isGrounded = true;
  isADS = false;

  // Energy system
  energy = PLAYER.MAX_ENERGY;
  maxEnergy = PLAYER.MAX_ENERGY;
  private energyRegenDelay = 0;
  energyDepleted = false; // true when energy ran out, blocks sprint until regen delay

  // W double-tap dash detection (PC)
  private lastWPressTime = 0;
  private dashActive = false;
  private dashTimer = 0;

  // Collision
  private colliders: THREE.Box3[] = [];

  constructor(camera: THREE.PerspectiveCamera, input: InputManager) {
    this.camera = camera;
    this.input = input;
  }

  setColliders(colliders: THREE.Box3[]): void {
    this.colliders = colliders;
  }

  update(dt: number): void {
    // Mouse look
    const mouseDelta = this.input.consumeMouseDelta();
    if (mouseDelta.x !== 0 || mouseDelta.y !== 0) {
      this.euler.setFromQuaternion(this.camera.quaternion);
      this.euler.y -= mouseDelta.x * PLAYER.MOUSE_SENSITIVITY;
      this.euler.x -= mouseDelta.y * PLAYER.MOUSE_SENSITIVITY;
      this.euler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.euler.x));
      this.camera.quaternion.setFromEuler(this.euler);
    }

    // Movement direction (keyboard + touch joystick)
    const direction = new THREE.Vector3();
    if (this.input.isKeyDown('KeyW')) direction.z -= 1;
    if (this.input.isKeyDown('KeyS')) direction.z += 1;
    if (this.input.isKeyDown('KeyA')) direction.x -= 1;
    if (this.input.isKeyDown('KeyD')) direction.x += 1;
    // Touch joystick (touchMoveX = left/right, touchMoveY = up/down on screen → forward/back)
    if (Math.abs(this.input.touchMoveX) > 0.1 || Math.abs(this.input.touchMoveY) > 0.1) {
      direction.x += this.input.touchMoveX;
      direction.z += this.input.touchMoveY;
    }
    direction.normalize();

    // State
    this.isADS = this.input.isADS();

    // W double-tap dash detection (PC only)
    if (this.input.isKeyJustPressed('KeyW')) {
      const now = performance.now() / 1000;
      if (now - this.lastWPressTime < PLAYER.DASH_DOUBLE_TAP_WINDOW) {
        this.dashActive = true;
        this.dashTimer = 0;
      }
      this.lastWPressTime = now;
    }
    // Dash stays active while W is held after double-tap
    if (this.dashActive && !this.input.isKeyDown('KeyW')) {
      this.dashActive = false;
    }

    // Sprint intent: Shift held, W double-tap dash, or mobile sprint toggle
    const wantsSprint = this.input.isKeyDown('ShiftLeft') || this.input.isKeyDown('ShiftRight')
      || this.dashActive || this.input.touchSprint;

    // Energy gating: can only sprint if enough energy and not in depleted cooldown
    const canSprint = wantsSprint && !this.isCrouching && !this.isADS
      && this.energy > 0 && !this.energyDepleted;
    this.isSprinting = canSprint;

    // Energy drain / regen
    if (this.isSprinting) {
      this.energy -= PLAYER.ENERGY_DRAIN_RATE * dt;
      this.energyRegenDelay = PLAYER.ENERGY_REGEN_DELAY;
      if (this.energy <= 0) {
        this.energy = 0;
        this.energyDepleted = true;
        this.isSprinting = false;
        this.dashActive = false;
        // Turn off mobile sprint toggle when depleted
        if (this.input.touchSprint) this.input.touchSprint = false;
      }
    } else {
      this.energyRegenDelay -= dt;
      if (this.energyRegenDelay <= 0) {
        this.energy = Math.min(this.maxEnergy, this.energy + PLAYER.ENERGY_REGEN_RATE * dt);
        // Allow sprinting again once enough energy regenerated
        if (this.energyDepleted && this.energy >= PLAYER.ENERGY_MIN_TO_SPRINT) {
          this.energyDepleted = false;
        }
      }
    }

    // Crouch toggle
    if (this.input.isKeyJustPressed('KeyC') || this.input.isKeyJustPressed('ControlLeft')) {
      this.isCrouching = !this.isCrouching;
    }

    // Speed calculation
    let speed: number = PLAYER.WALK_SPEED;
    if (this.isSprinting) {
      speed = PLAYER.SPRINT_SPEED;
    }
    if (this.isCrouching) {
      speed = PLAYER.CROUCH_SPEED;
    }
    if (this.isADS) {
      speed *= PLAYER.ADS_SPEED_MULTIPLIER;
    }

    // Camera-relative movement
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();

    const right = new THREE.Vector3();
    right.crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();

    this.velocity.x = (forward.x * -direction.z + right.x * direction.x) * speed;
    this.velocity.z = (forward.z * -direction.z + right.z * direction.x) * speed;

    // Jump
    if (this.input.isKeyJustPressed('Space') && this.isGrounded) {
      this.velocity.y = PLAYER.JUMP_FORCE;
      this.isGrounded = false;
    }

    // Gravity
    this.velocity.y += PLAYER.GRAVITY * dt;

    // Calculate new position
    const newPos = this.camera.position.clone();
    newPos.x += this.velocity.x * dt;
    newPos.z += this.velocity.z * dt;
    newPos.y += this.velocity.y * dt;

    // Simple collision check with boxes
    const playerRadius = 0.3;
    const playerBox = new THREE.Box3(
      new THREE.Vector3(newPos.x - playerRadius, newPos.y - PLAYER.HEIGHT, newPos.z - playerRadius),
      new THREE.Vector3(newPos.x + playerRadius, newPos.y, newPos.z + playerRadius)
    );

    let collisionX = false;
    let collisionZ = false;

    for (const collider of this.colliders) {
      // Check X collision
      const testBoxX = new THREE.Box3(
        new THREE.Vector3(newPos.x - playerRadius, this.camera.position.y - PLAYER.HEIGHT, this.camera.position.z - playerRadius),
        new THREE.Vector3(newPos.x + playerRadius, this.camera.position.y, this.camera.position.z + playerRadius)
      );
      if (testBoxX.intersectsBox(collider)) {
        collisionX = true;
      }

      // Check Z collision
      const testBoxZ = new THREE.Box3(
        new THREE.Vector3(this.camera.position.x - playerRadius, this.camera.position.y - PLAYER.HEIGHT, newPos.z - playerRadius),
        new THREE.Vector3(this.camera.position.x + playerRadius, this.camera.position.y, newPos.z + playerRadius)
      );
      if (testBoxZ.intersectsBox(collider)) {
        collisionZ = true;
      }
    }

    if (!collisionX) this.camera.position.x = newPos.x;
    if (!collisionZ) this.camera.position.z = newPos.z;
    this.camera.position.y = newPos.y;

    // Ground check
    const targetHeight = this.isCrouching ? PLAYER.CROUCH_HEIGHT : PLAYER.HEIGHT;
    if (this.camera.position.y < targetHeight) {
      this.camera.position.y = targetHeight;
      this.velocity.y = 0;
      this.isGrounded = true;
    }

    // ADS FOV
    const targetFOV = this.isADS ? PLAYER.FOV_ADS : PLAYER.FOV_NORMAL;
    this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, targetFOV, dt * 10);
    this.camera.updateProjectionMatrix();
  }

  getPosition(): THREE.Vector3 {
    return this.camera.position.clone();
  }

  getDirection(): THREE.Vector3 {
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    return dir;
  }
}
